import { useEffect, useState } from 'react';
import { useMediaQuery } from './useMediaQuery';
import {
  annotateStackedTable, markStackedTableWrapper, toggleStackedRowFromEvent,
} from '../utils/stackedTable';

/**
 * En el celular muestra cada fila de una tabla ancha como tarjeta, sin tocar
 * el JSX de las filas (botones, selects y permisos siguen siendo los mismos).
 * En escritorio no hace nada.
 *
 * Uso:
 *   const stacked = useStackedTable({ primary: ['Especie', 'Monto', 'Estado'], title: 'Cliente', actions: 'Acciones' });
 *   <table ref={stacked.ref} className={stacked.className} style={...}>
 *
 * Ver annotateStackedTable para el significado de cada opción. Las etiquetas
 * son el texto de los encabezados de la tabla.
 */
export const useStackedTable = ({ primary = [], title, actions } = {}) => {
  const isMobile = useMediaQuery();
  const [table, setTable] = useState(null);
  const configKey = JSON.stringify({ primary, title, actions });

  useEffect(() => {
    if (!isMobile || !table) return undefined;
    const config = JSON.parse(configKey);
    let frame = null;
    const apply = () => {
      frame = null;
      annotateStackedTable(table, config);
    };
    markStackedTableWrapper(table);
    apply();

    // Las filas cambian en vivo (Firestore, filtros, orden, permisos): se
    // re-anotan en el siguiente frame, una sola vez por tanda de cambios.
    const observer = new MutationObserver(() => {
      if (frame === null) frame = window.requestAnimationFrame(apply);
    });
    observer.observe(table, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['disabled'],
    });
    table.addEventListener('click', toggleStackedRowFromEvent);

    return () => {
      observer.disconnect();
      table.removeEventListener('click', toggleStackedRowFromEvent);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [isMobile, table, configKey]);

  return { ref: setTable, className: isMobile ? 'axon-stacked' : undefined, isMobile };
};
